import { Leaf } from "lucide-react";
import { cn } from "@/lib/utils";

const MARK_SIZE = {
  sm: "size-7 [&_svg]:size-3.5",
  md: "size-8 [&_svg]:size-4.5",
  lg: "size-12 [&_svg]:size-6",
} as const;

export interface BrandMarkProps {
  size?: keyof typeof MARK_SIZE;
  /** `paper`: the light seal on the forest band (login). */
  tone?: "brand" | "paper";
  className?: string;
}

/** The leaf seal. Brand green is for the seal, the primary action and focus. */
export function BrandMark({
  size = "md",
  tone = "brand",
  className,
}: BrandMarkProps) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-grid flex-none place-items-center rounded-full",
        MARK_SIZE[size],
        tone === "paper"
          ? "text-brand-deep bg-white"
          : "bg-primary text-primary-foreground",
        className,
      )}
    >
      <Leaf />
    </span>
  );
}

export interface BrandLockupProps {
  size?: "md" | "lg";
  /** `band`: light type on the forest band. */
  tone?: "default" | "band";
  /** Hide the wordmark (collapsed sidebar); the name stays for AT. */
  markOnly?: boolean;
  className?: string;
}

/** Leaf seal plus the two-line wordmark "Johnston's / Regenerative". */
export function BrandLockup({
  size = "md",
  tone = "default",
  markOnly,
  className,
}: BrandLockupProps) {
  const band = tone === "band";
  return (
    <span
      className={cn("inline-flex items-center gap-2.5", className)}
      // One named logo for AT, with or without the wordmark showing, the
      // same name the phone's leaf seal carries.
      aria-label="Johnston's Regenerative"
      role="img"
    >
      <BrandMark
        size={size === "lg" ? "lg" : "md"}
        tone={band ? "paper" : "brand"}
      />
      {!markOnly && (
        <span
          className={cn(
            "flex flex-col leading-tight",
            size === "lg" ? "text-section" : "text-compact",
          )}
        >
          <span className="font-semibold">Johnston&apos;s</span>
          <span
            className={cn(
              "font-normal",
              band ? "text-white/80" : "text-secondary-foreground",
            )}
          >
            Regenerative
          </span>
        </span>
      )}
    </span>
  );
}
